import { of, EMPTY, concat } from 'rxjs';
import { ofType } from 'redux-observable';
import { mergeMap, catchError } from 'rxjs/operators';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as Actions from './label-que.actions';
import * as LabelQueApi from './label-que.api';
import Global from '../../store/global-selectors';
import * as BatchPrintActions from '../../store/batches/print/batches.print.actions';
import * as BatchesGridApi from '../../store/batches/grid/batches.grid.api';
import * as PrintActions from '../../store/printing/print.actions';
import { bulkloadFulfillmentPlans } from '../../store/bulkload/bulkload.actions';
import * as DocumentActions from '../../store/documents/documents.actions';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { DocumentService, isPrintSettingSetToDownload } from '../../services/document-service';
import { DocumentType } from '../../enums/document-type';
import { generateFulfillmentPlanSummaryDocument } from '../printing/print.api';
import { openPrintModal, closingPrintModal, documentDownloadStarted } from '../printing/print.actions';
import { labelBatchStatusModalArchiveBatchesSuccess } from '../batches/label-batch-status-modal/batches.label-batch-status-modal.actions';
import { getPrintSettingAndCheckIsOfflinePrinter } from '../shipstation-connect/ss-connect.helpers';
import { createBatchPickListsAsync } from '../printing/async-printing/batch-pick-lists';
import { downloadDocument } from '../documents/document.helpers';

import { messages } from './messages';

export const getLabelQueBatchesEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.LABEL_QUE_GET_BATCHES_REQUEST, Actions.LOAD_MORE_LABEL_QUE_BATCHES),
    mergeMap(({ labelBatchUserId }) => {
      const userId = Global.selectUserCurrentUserId(getState());
      if (labelBatchUserId && labelBatchUserId !== userId) {
        return EMPTY;
      }
      const fromProcessDate = Global.selectLabelQueFromProcessDate(getState());
      const pageSize = Global.selectLabelQuePageSize(getState());
      return LabelQueApi.getLabelQueBatches(userId, fromProcessDate, pageSize).pipe(
        mergeMap((response) => {
          const { pageData } = response;
          return of(Actions.getLabelQueBatchesSuccess(pageData));
        }),
        catchError((error) => of(Actions.getLabelQueBatchesFailure(error))),
      );
    }),
  );

export const labelQueArchiveBatches = (action$) => {
  return action$.pipe(
    ofType(Actions.LABEL_QUE_ARCHIVE_BATCHES_REQUEST),
    mergeMap((action) => {
      const { batchIds } = action;
      return BatchesGridApi.archiveBatches(batchIds).pipe(
        mergeMap(() => {
          const actions = [
            Actions.labelQueArchiveBatchesSuccess(),
            labelBatchStatusModalArchiveBatchesSuccess(batchIds),
            Actions.getLabelQueBatchesRequest(),
          ];
          return of(...actions);
        }),
        catchError((e) => of(Actions.labelQueArchiveBatchesFailure(e))),
      );
    }),
  );
};

export const getLabelQueBatchWarnAndError = (action$) =>
  action$.pipe(
    ofType(Actions.LABEL_QUE_GET_WARNING_AND_ERROR_REQUEST),
    mergeMap((action) => {
      const { batchId } = action;
      return LabelQueApi.getLabelQueBatchWarningAndError(batchId).pipe(
        mergeMap((response) => {
          const { data = [] } = response;
          const fulfillmentPlanIds = data.map((datum) => datum.fulfillmentPlanId);
          return concat(of(Actions.labelQueGetWarningAndErrorSuccess(response.data)), of(bulkloadFulfillmentPlans(fulfillmentPlanIds)));
        }),
        catchError((error) => of(Actions.labelQueGetWarningAndErrorFailure(error))),
      );
    }),
  );

export const beginLabelQueBatchPackingSlipPrintingEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.LABEL_QUE_PRINT_PACKING_SLIP_START),
    mergeMap((action) => {
      const { batchId } = action;
      const state = getState();
      const selectedBatches = batchId ? [batchId] : Global.selectLabelQueSelectedBatches(state);

      return of(BatchPrintActions.startPackingSlipPrintedStatusCheck(selectedBatches, action.autoPrint));
    }),
  );
};

export const beginBatchQueLabelPrintingEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.LABEL_QUE_PRINT_LABEL_START),
    mergeMap((action) => {
      const { batchId } = action;
      const state = getState();
      const selectedBatches = batchId ? [batchId] : Global.selectLabelQueSelectedBatches(state);
      return of(BatchPrintActions.startLabelPrintedCheck(selectedBatches, action.autoPrint));
    }),
  );
};

export const labelQuePrintPickListEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.LABEL_QUE_PRINT_PICK_LIST_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const { batchId } = action;
      const selectedBatches = batchId ? [batchId] : Global.selectLabelQueSelectedBatches(state);
      if (selectedBatches.length === 0) {
        return EMPTY;
      }

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.BatchPickList.printSettingKey,
      });

      if (isOfflinePrinter) {
        return EMPTY;
      }

      return createBatchPickListsAsync({
        fulfillmentPlanSetIds: selectedBatches,
        printer: printSetting,
        documentCount: selectedBatches.length,
      }).pipe(
        mergeMap((printJob) => {
          if (isPrintSettingSetToDownload(printSetting)) {
            const downloadActions = downloadDocument(printJob.printUrl, DocumentType.BatchPickList, printJob.ids);
            return of(...downloadActions);
          }

          if (printSetting && !printSetting.disabled) {
            const printActions = DocumentService.getPrintDocumentActions(
              printJob.printer,
              DocumentType.BatchPickList,
              printJob.ids,
              undefined,
              printJob.printUrl,
            );
            return of(...printActions);
          }

          return of(PrintActions.openPrintModal(printJob.printUrl, DocumentType.BatchPickList.identifier, printJob.ids));
        }),
      );
    }),
  );
};

export const labelQuePrintShipmentManifestEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.LABEL_QUE_PRINT_SHIPMENT_MANIFEST_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const { batchId } = action;
      const selectedBatches = batchId ? [batchId] : Global.selectLabelQueSelectedBatches(state);
      if (selectedBatches.length === 0) {
        return EMPTY;
      }

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.BatchShipmentManifest.printSettingKey,
      });

      if (isOfflinePrinter) {
        return EMPTY;
      }

      if (isPrintSettingSetToDownload(printSetting)) {
        return of(DocumentActions.downloadDocument(DocumentType.BatchShipmentManifest, selectedBatches));
      }

      if (printSetting && !printSetting.disabled) {
        return of(
          DocumentActions.printDocument(
            DocumentType.BatchShipmentManifest,
            selectedBatches,
            DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.BatchShipmentManifest, false),
          ),
        );
      }

      const url = `${DocumentType.BatchShipmentManifest.apiDownloadPath}/${selectedBatches}`;
      return of(PrintActions.openPrintModal(url, DocumentType.BatchShipmentManifest.identifier, selectedBatches));
    }),
  );
};

export const labelQuePrintOrderSummaryEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.LABEL_QUE_PRINT_ORDER_SUMMARY),
    mergeMap(({ batchId }) => {
      const state = getState();
      const selectedBatches = batchId ? [batchId] : Global.selectLabelQueSelectedBatches(state);

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.OrderSummary.printSettingKey,
      });

      if (isOfflinePrinter) {
        return EMPTY;
      }

      return generateFulfillmentPlanSummaryDocument(selectedBatches).pipe(
        mergeMap(({ value: url }) => {
          if (isPrintSettingSetToDownload(printSetting)) {
            const downloadUrl = url.includes('?') ? `${url}&download=1` : `${url}?download=1`;
            DocumentService.downloadFile(downloadUrl);
            return of(documentDownloadStarted('order-summary', 1));
          }
          if (printSetting && !printSetting.disabled) {
            return of(
              DocumentActions.printDocument(
                DocumentType.OrderSummary,
                batchId,
                DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.OrderSummary, false),
                undefined,
                url,
              ),
            );
          }
          return of(openPrintModal(url, DocumentType.OrderSummary.identifier));
        }),
        catchError(() => {
          const orderSummaryErrorProps = {
            title: <FormattedMessage {...messages.errorCreatingOrderSummary} />,
            content: <FormattedMessage {...messages.unknownError} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
          };
          NotificationService.showNotification(orderSummaryErrorProps);
          return of(closingPrintModal());
        }),
      );
    }),
  );

export const handleGeneralLabelQueExceptionEpic = (action$) => {
  return action$.pipe(
    ofType(
      Actions.GET_LABEL_QUE_BATCHES_FAILURE,
      Actions.LABEL_QUE_ARCHIVE_BATCHES_FAILURE,
      Actions.LABEL_QUE_GET_WARNING_AND_ERROR_FAILURE,
    ),
    mergeMap(() => {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.errorNotoTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        dontHide: true,
        content: <FormattedMessage {...messages.errorNotoBody} />,
      });
      return EMPTY;
    }),
  );
};

export const retryBatchForWarnAndErrorEpic = (action$) =>
  action$.pipe(
    ofType(Actions.ON_RETRY_BATCH_REQUEST),
    mergeMap((action) => {
      const { batchId } = action;
      return LabelQueApi.retryBatch(batchId).pipe(
        mergeMap(() => EMPTY),
        catchError(() => EMPTY),
      );
    }),
  );
